import { postFavoritePairing, StatusCode } from '@hungryroot/rest';
import { pairingInteraction } from '@hungryroot/segment';
import { type FunnelFavoritePairing } from './deriveFunnelFavoritePairings';

export interface PostFunnelFavoritesResult {
  derived: number;
  succeeded: number;
  failed: number;
}

/**
 * Best-effort, fire-and-forget favoriting of the pairings a user positively
 * swiped in the funnel deck, run once on signup. For each
 * favorite this mirrors `trackFavorite`'s pairing branch
 * (`packages/favorites/src/segment/track-favorite.ts`) by calling
 * `pairingInteraction` directly — keep the two
 * in sync; not imported to avoid pulling `@hungryroot/favorites` into the
 * signup bundle — then POSTs the favorite. Never throws: every item is
 * settled independently so one failure can't block the others or the
 * post-signup redirect.
 */
export async function postFunnelFavorites(
  favorites: FunnelFavoritePairing[]
): Promise<PostFunnelFavoritesResult> {
  const results = await Promise.allSettled(
    favorites.map(async ({ pairingId, name }) => {
      pairingInteraction({
        pairingId,
        pairingName: name,
        actionType: 'saved',
        location: null,
      });

      const res = await postFavoritePairing({
        body: { pairing_id: pairingId },
      });
      if (res.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(res.data, null, 2));
      }
    })
  );

  const succeeded = results.filter(
    (result) => result.status === 'fulfilled'
  ).length;

  return {
    derived: favorites.length,
    succeeded,
    failed: favorites.length - succeeded,
  };
}
